import { createSelector } from '@reduxjs/toolkit';
import { Map } from 'immutable';
import { z } from 'zod';
import { getStateLocationFilter } from '@/scheduler/selectors/filters';
import { getCurrentRange } from '@/scheduler/selectors/getCurrentViewMode';
import {
  DEFAULT_SORT,
  SORT_BY_CUSTOM,
  SORT_BY_FIRST_NAME,
  SORT_BY_LAST_NAME,
  SORT_BY_START_TIME,
} from '@/scheduler/util/constants';
import { DAY_RANGE, MONTH_RANGE, TWO_WEEK_RANGE, WEEK_RANGE } from '@/scheduler/util/view';
import type { WiwState } from '@/store';

const SchedulerSortOptionSchema = z.enum([
  SORT_BY_FIRST_NAME,
  SORT_BY_LAST_NAME,
  SORT_BY_START_TIME,
  SORT_BY_CUSTOM,
  DEFAULT_SORT,
]);

export type SchedulerSortOption = z.infer<typeof SchedulerSortOptionSchema>;

export const LocationSortSchema = z.object({
  [DAY_RANGE]: SchedulerSortOptionSchema,
  [WEEK_RANGE]: SchedulerSortOptionSchema,
  [TWO_WEEK_RANGE]: SchedulerSortOptionSchema,
});

export type LocationSort = z.infer<typeof LocationSortSchema>;

export const DEFAULT_LOCATION_SORT_FILTER: LocationSort = {
  [DAY_RANGE]: DEFAULT_SORT,
  [WEEK_RANGE]: DEFAULT_SORT,
  [TWO_WEEK_RANGE]: DEFAULT_SORT,
};

export const getStateLocationSort = (state: WiwState) =>
  state.scheduler.get('locationSort', Map()) as Map<string, LocationSort>;

export const getCurrentLocationSort = createSelector(
  [getStateLocationFilter, getStateLocationSort],
  (locationId, locationSortFilters) => {
    return locationSortFilters.get(`${locationId}`, DEFAULT_LOCATION_SORT_FILTER);
  },
);

export const getLocationRangeSort = createSelector(
  [getCurrentLocationSort, getCurrentRange],
  (locationSort, currentRange) => {
    return currentRange === MONTH_RANGE ? DEFAULT_SORT : (locationSort[currentRange] ?? DEFAULT_SORT);
  },
);
